import React, { useContext } from 'react';
import { WidgetPanelDetailProps } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanel';
import JsonPanel from 'utils/components/JsonPanel/JsonPanel';
import { getVenueAnalysisData } from 'widgets/venueAnalysis/venueAnalysisAggregatedData';
import {
    initialVenueAnalysisConfigurationState,
    VenueAnalysisConfigurationState,
} from 'widgets/venueAnalysis/store/configuration/VenueAnalysisConfigurationState';
import {
    initialVenueAnalysisComponentState,
    VenueAnalysisComponentState,
} from 'widgets/venueAnalysis/store/component/VenueAnalysisComponentState';
import { getWidgetStreamRequestInfo } from 'widgets/venueAnalysis/store/stream/VenueAnalysisStreamRequest';
import AppContext from 'app/AppContext';
import { WidgetPanelGrid } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanelGrid';
import {
    initialVenueAnalysisDataState,
    VenueAnalysisDataState,
} from 'widgets/venueAnalysis/store/stream/VenueAnalysisStreamDataState';

export interface WidgetPanelVenueAnalysisProps extends WidgetPanelDetailProps {
    mode:
        | 'data'
        | 'timeSeries'
        | 'orderInfo'
        | 'qtyInfo'
        | 'fillInfo'
        | 'mktVolHist'
        | 'mktVolRt';
}

export default function WidgetPanelVenueAnalysis(
    props: WidgetPanelVenueAnalysisProps
) {
    const {
        widgetState,
        state,
        mode,
        widget: { type: widgetType, id: widgetKey },
    } = props;
    const {
        services: { portfolioService },
    } = useContext(AppContext);

    const activeInputUsesOrdersStream =
        !!portfolioService.selectPortfolioActiveInput(state.state)
            ?.usesOrdersStream;

    const request = getWidgetStreamRequestInfo(
        state.state,
        {
            widgetType,
            widgetKey,
        },
        activeInputUsesOrdersStream
    )!;

    const streamKey = request?.streamKey;

    const config =
        (widgetState?.config as VenueAnalysisConfigurationState) ??
        initialVenueAnalysisConfigurationState;
    const streamState: VenueAnalysisDataState =
        state.state.streams.data['venueAnalysis'][streamKey]?.data ??
        initialVenueAnalysisDataState;

    const component =
        (widgetState?.component as VenueAnalysisComponentState) ??
        initialVenueAnalysisComponentState;

    const data =
        streamState &&
        getVenueAnalysisData(
            streamState,
            component.selection,
            config.includePreviousDaysFills
        );

    switch (mode) {
        case 'timeSeries':
            return (
                <WidgetPanelGrid
                    key="timeSeries"
                    data={streamState?.timeSeries ?? []}
                    getRowDataId={(d) => d._i.toString()}
                    csvFileName={`${widgetKey}-timeSeries`}
                />
            );
        case 'fillInfo':
            return <JsonPanel key="fillInfo" data={streamState.fillInfo!} />;
        case 'mktVolHist':
            return (
                <JsonPanel key="mktVolHist" data={streamState.mktVolHist!} />
            );
        case 'mktVolRt':
            return <JsonPanel key="mktVolRt" data={streamState.mktVolHist!} />;
        case 'qtyInfo':
            return <JsonPanel key="qtyInfo" data={streamState!.qtyInfo!} />;
        case 'orderInfo':
            return <JsonPanel key="orderInfo" data={streamState!.orderInfo!} />;
        default:
            return (
                <div className="widget-panel">
                    <div className="title">Venue Analysis Data</div>
                    <div className="field">
                        <span className={'label'}>Stream Key:</span>
                        {streamKey}
                    </div>
                    <JsonPanel data={data} />
                </div>
            );
    }
}

export function addVenueAnalysisPanes(
    detailProps: WidgetPanelDetailProps,
    panes: { menuItem: string; render: () => JSX.Element }[]
) {
    const renderData = () => (
        <WidgetPanelVenueAnalysis {...detailProps} mode={'data'} />
    );
    panes.push({
        menuItem: 'Data',
        render: renderData,
    });

    const renderTs = () => (
        <WidgetPanelVenueAnalysis {...detailProps} mode={'timeSeries'} />
    );
    panes.push({
        menuItem: 'Stream',
        render: renderTs,
    });

    const renderOi = () => (
        <WidgetPanelVenueAnalysis {...detailProps} mode={'orderInfo'} />
    );
    panes.push({
        menuItem: 'Order Info',
        render: renderOi,
    });

    const renderFi = () => (
        <WidgetPanelVenueAnalysis {...detailProps} mode={'fillInfo'} />
    );
    panes.push({
        menuItem: 'Fill Info',
        render: renderFi,
    });

    const renderQi = () => (
        <WidgetPanelVenueAnalysis {...detailProps} mode={'qtyInfo'} />
    );
    panes.push({
        menuItem: 'Qty Info',
        render: renderQi,
    });

    const renderH = () => (
        <WidgetPanelVenueAnalysis {...detailProps} mode={'mktVolHist'} />
    );
    panes.push({
        menuItem: 'Mkt Vol Hist',
        render: renderH,
    });

    const renderR = () => (
        <WidgetPanelVenueAnalysis {...detailProps} mode={'mktVolRt'} />
    );
    panes.push({
        menuItem: 'Mkt Vol Rt',
        render: renderR,
    });
}
